import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Globe, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Overline, Rule, Watermark } from "@/components/site/Primitives";
import { btnPrimary } from "@/components/site/buttons";

const AREAS = [
  "Strategic Planning",
  "Business Consulting",
  "Management & Operational Advisory",
  "Risk & Governance Review",
  "Personal Financial Planning",
  "General Inquiry",
];

const EMPTY = { name: "", email: "", company: "", phone: "", area: "", message: "", website: "" };

export default function Contact() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => setC(r.data.company)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Please enter a valid email.";
    if (!form.area) err.area = "Please choose an area of interest.";
    if (!form.message.trim()) err.message = "Please enter a message.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/inquiries", form);
      if (data.ok) {
        setDone(true);
        setForm(EMPTY);
        toast.success(data.message || "Your inquiry has been received.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "mt-2 w-full rounded-none border border-ink/20 bg-cream-100 px-4 py-3 font-sans text-sm text-ink placeholder:text-steel-400 focus:border-gold-500 focus:outline-none";
  const label = "font-sans text-sm font-medium text-ink";

  return (
    <>
      <Seo title="Contact" description="Request a consultation with Goldman Fischer. Telephone 631.804.0212 · PO Box 319, Centerport, NY 11721." />

      <section className="relative overflow-hidden border-b border-gold-500/20 bg-cream-200/40">
        <Watermark className="-right-6 top-2 text-[13rem]">Contact</Watermark>
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-10">
          <Overline>Request a Consultation</Overline>
          <h1 className="mt-4 font-serif text-5xl font-light text-ink lg:text-6xl">Let’s talk it through</h1>
          <Rule className="mt-6" />
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-16 px-5 py-20 lg:grid-cols-5 lg:px-10">
        <div className="lg:col-span-2">
          <h2 className="font-serif text-3xl font-light text-ink">Goldman Fischer</h2>
          <p className="mt-4 font-sans leading-relaxed text-steel-600">
            Reach out to discuss your situation. Inquiries are reviewed personally and handled in confidence.
          </p>
          <ul className="mt-8 space-y-5 font-sans text-ink">
            {c && (
              <>
                <li className="flex items-start gap-4">
                  <Phone className="mt-1 h-5 w-5 text-gold-500" />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.18em] text-steel-400">Telephone</span>
                    <a href={`tel:${c.phone_link}`} className="text-lg gf-link hover:text-gold-600" data-testid="contact-phone-link">{c.phone}</a>
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="mt-1 h-5 w-5 text-gold-500" />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.18em] text-steel-400">Office</span>
                    <span className="text-lg">{c.address}</span>
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <Globe className="mt-1 h-5 w-5 text-gold-500" />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.18em] text-steel-400">Website</span>
                    <span className="text-lg">{c.website}</span>
                  </span>
                </li>
              </>
            )}
          </ul>
        </div>

        <div className="lg:col-span-3">
          {done ? (
            <div className="flex flex-col items-center border border-gold-500/40 bg-cream-200/50 px-8 py-20 text-center" data-testid="inquiry-success">
              <CheckCircle2 className="h-12 w-12 text-gold-500" strokeWidth={1.25} />
              <h3 className="mt-6 font-serif text-3xl font-light text-ink">Thank you</h3>
              <p className="mt-3 max-w-md font-sans leading-relaxed text-steel-600">
                Your inquiry has been received and stored securely. George will review it and respond personally.
              </p>
              <button onClick={() => setDone(false)} className="mt-8 font-sans text-sm text-gold-600 gf-link" data-testid="inquiry-new">Send another inquiry</button>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-ink/10 bg-cream-100 p-8 lg:p-10" data-testid="inquiry-form" noValidate>
              {/* Honeypot */}
              <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className={label} htmlFor="name">Name</label>
                  <input id="name" value={form.name} onChange={set("name")} className={field} placeholder="Your full name" data-testid="inquiry-name" />
                  {errors.name && <p className="mt-1 font-sans text-xs text-destructive">{errors.name}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="email">Email</label>
                  <input id="email" type="email" value={form.email} onChange={set("email")} className={field} placeholder="you@company.com" data-testid="inquiry-email" />
                  {errors.email && <p className="mt-1 font-sans text-xs text-destructive">{errors.email}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="company">Company <span className="text-steel-400">(optional)</span></label>
                  <input id="company" value={form.company} onChange={set("company")} className={field} placeholder="Company name" data-testid="inquiry-company" />
                </div>
                <div>
                  <label className={label} htmlFor="phone">Phone <span className="text-steel-400">(optional)</span></label>
                  <input id="phone" value={form.phone} onChange={set("phone")} className={field} placeholder="(000) 000-0000" data-testid="inquiry-phone" />
                </div>
              </div>

              <div className="mt-6">
                <label className={label} htmlFor="area">Area of interest</label>
                <select id="area" value={form.area} onChange={set("area")} className={field} data-testid="inquiry-area">
                  <option value="">Select an area…</option>
                  {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {errors.area && <p className="mt-1 font-sans text-xs text-destructive">{errors.area}</p>}
              </div>

              <div className="mt-6">
                <label className={label} htmlFor="message">Message</label>
                <textarea id="message" rows={5} value={form.message} onChange={set("message")} className={field} placeholder="Briefly describe what you’d like to discuss." data-testid="inquiry-message" />
                {errors.message && <p className="mt-1 font-sans text-xs text-destructive">{errors.message}</p>}
              </div>

              <button type="submit" disabled={submitting} className={`mt-8 w-full ${btnPrimary} disabled:opacity-60`} data-testid="inquiry-submit">
                {submitting ? "Sending…" : "Request a Consultation"}
              </button>
              <p className="mt-4 text-center font-sans text-xs text-steel-400">
                Submitting this form does not create a client engagement. See our <a href="/privacy" className="underline">Privacy Policy</a>.
              </p>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
